import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Label } from '../../../components/ui/label';
import { Loader2 } from 'lucide-react';
import { updatePipeFlags, type UpdatePipeFlagsPayload } from '../../../services/pipeIdEditApi';
import { Notification as customNotification } from '../../../components/ui/custom-notification';

export type PipeFlagMode = 'archive' | 'flange' | 'anode';

export interface UpdatePipeFlagsDialogPipe {
  id: number | string;
  pipeID: string;
  isArchive?: boolean | number;
  isFlange?: boolean | number;
  isAnode?: boolean | number;
}

interface UpdatePipeFlagsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pipes: UpdatePipeFlagsDialogPipe[];
  /** Which flag this dialog edits. The other flag is left unchanged. */
  mode: PipeFlagMode;
  onUpdated?: () => void;
}

function flagOn(value: unknown): boolean {
  return value === true || value === 1 || value === '1';
}

const MODE_META: Record<
  PipeFlagMode,
  { title: string; label: string; pick: (pipe: UpdatePipeFlagsDialogPipe) => unknown }
> = {
  archive: {
    title: 'Archive',
    label: 'Mark as Archived',
    pick: (pipe) => pipe.isArchive,
  },
  flange: {
    title: 'Flange',
    label: 'Mark as Flange',
    pick: (pipe) => pipe.isFlange,
  },
  anode: {
    title: 'Anode',
    label: 'Mark as Anode',
    pick: (pipe) => pipe.isAnode,
  },
};

function flagPayloadForMode(mode: PipeFlagMode, nextValue: 0 | 1): Pick<UpdatePipeFlagsPayload, 'isArchive' | 'isFlange' | 'isAnode'> {
  if (mode === 'archive') return { isArchive: nextValue };
  if (mode === 'flange') return { isFlange: nextValue };
  return { isAnode: nextValue };
}

export const UpdatePipeFlagsDialog: React.FC<UpdatePipeFlagsDialogProps> = ({
  open,
  onOpenChange,
  pipes,
  mode,
  onUpdated,
}) => {
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  const { title, label } = MODE_META[mode];
  const isBulk = pipes.length > 1;

  useEffect(() => {
    if (!open || pipes.length === 0) return;
    const read = MODE_META[mode].pick;
    setChecked(pipes.every((pipe) => flagOn(read(pipe))));
  }, [open, pipes, mode]);

  const handleSave = async () => {
    if (pipes.length === 0) return;

    const ids = pipes
      .map((pipe) => Number(pipe.id))
      .filter((id) => Number.isFinite(id) && id > 0);

    if (ids.length === 0) return;

    const nextValue = (checked ? 1 : 0) as 0 | 1;
    const changedIds = pipes
      .filter((pipe) => {
        const statusId = Number(pipe.id);
        if (!Number.isFinite(statusId) || statusId <= 0) return false;
        const prev = flagOn(MODE_META[mode].pick(pipe)) ? 1 : 0;
        return prev !== nextValue;
      })
      .map((pipe) => Number(pipe.id));

    if (changedIds.length === 0) {
      customNotification.error('No change', `Change ${title.toLowerCase()} before saving.`);
      return;
    }

    setSaving(true);
    try {
      await updatePipeFlags({
        ids: changedIds,
        ...flagPayloadForMode(mode, nextValue),
      });
      customNotification.success(
        `${title} updated`,
        changedIds.length === 1
          ? '1 pipe updated.'
          : `${changedIds.length} pipes updated.`
      );
      onUpdated?.();
      onOpenChange(false);
    } catch (error: any) {
      customNotification.error(
        'Update failed',
        error?.response?.data?.message || error?.message || 'Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent className="bg-white max-w-md w-full">
        <DialogHeader className="pl-0 pr-14 pb-4">
          <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {isBulk ? (
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-[13px] text-heritage-grey mb-2">
                {pipes.length} pipe{pipes.length !== 1 ? 's' : ''} selected:
              </p>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {pipes.map((pipe) => (
                  <div
                    key={String(pipe.id ?? pipe.pipeID)}
                    className="text-sm text-gray-700 py-1 flex items-center"
                  >
                    <span className="w-2 h-2 bg-gray-400 rounded-full mr-2 flex-shrink-0" />
                    Pipe ID: {pipe.pipeID || '—'}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-[13px] text-heritage-grey">
              Pipe <span className="font-semibold text-text-grey">{pipes[0]?.pipeID || '—'}</span>
            </p>
          )}
          <label className="flex items-center gap-3 cursor-pointer">
            <Checkbox
              checked={checked}
              onCheckedChange={(value) => setChecked(value === true)}
              disabled={saving}
            />
            <Label className="text-[13px] font-semibold text-blueprint-blue cursor-pointer">
              {label}
            </Label>
          </label>
        </div>
        <DialogFooter className="gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="min-w-[100px]"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving || pipes.length === 0}
            className="bg-hero-red hover:bg-hero-red/90 text-white min-w-[100px]"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving…
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
